(function () {
  const READY_CLASS = "is-marquee-ready";
  const DEFAULT_SPEED = 88;

  function onReady(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  }

  function getNumber(value, fallback) {
    const number = parseFloat(value);

    return Number.isFinite(number) ? number : fallback;
  }

  function getTrackGap(track) {
    const styles = window.getComputedStyle(track);

    return getNumber(styles.columnGap || styles.gap, 0);
  }

  function getBaseItems(track) {
    return Array.from(
      track.querySelectorAll("[data-trustmarks-marquee-item]"),
    ).filter((item) => item.dataset.trustmarksClone !== "true");
  }

  function getAllItems(track) {
    return Array.from(track.querySelectorAll("[data-trustmarks-marquee-item]"));
  }

  function clearClones(track) {
    track
      .querySelectorAll('[data-trustmarks-clone="true"]')
      .forEach((clone) => clone.remove());
  }

  function cloneItems(track, baseItems, cloneSetCount) {
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < cloneSetCount; index += 1) {
      baseItems.forEach((item) => {
        const clone = item.cloneNode(true);

        clone.dataset.trustmarksClone = "true";
        clone.setAttribute("aria-hidden", "true");
        clone.querySelectorAll("img").forEach((image) => {
          image.setAttribute("alt", "");
        });
        fragment.appendChild(clone);
      });
    }

    track.appendChild(fragment);
  }

  function measureLoopDistance(track, baseItems) {
    const gap = getTrackGap(track);
    const itemWidth = baseItems.reduce(
      (width, item) => width + item.offsetWidth,
      0,
    );

    return Math.ceil(itemWidth + gap * baseItems.length);
  }

  function initTrustmarkMarquee(outer, options = {}) {
    if (!outer || outer.dataset.trustmarksMarqueeInit === "true") return;

    const track = outer.querySelector("[data-trustmarks-marquee-track]");
    if (!track) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const speed = getNumber(
      outer.dataset.trustmarksSpeed || options.speed,
      DEFAULT_SPEED,
    );
    const shouldScale =
      options.scale === true || outer.dataset.trustmarksScale === "true";
    let rebuildFrame = null;
    let scaleFrame = null;

    outer.dataset.trustmarksMarqueeInit = "true";

    function updateScales() {
      if (!shouldScale) return;

      const outerRect = outer.getBoundingClientRect();
      const centerX = outerRect.left + outerRect.width / 2;
      const maxDistance = outerRect.width * 0.65;

      getAllItems(track).forEach((item) => {
        const rect = item.getBoundingClientRect();
        const itemCenterX = rect.left + rect.width / 2;
        const distance = Math.abs(itemCenterX - centerX);
        const progress =
          maxDistance > 0 ? Math.min(distance / maxDistance, 1) : 1;
        const scale = Math.max(1.2 - progress * 0.45, 0.75);

        item.style.setProperty("--trustmarks-scale", scale.toFixed(4));
        item.style.zIndex = String(Math.round(scale * 100));
      });
    }

    function runScaleLoop() {
      updateScales();

      if (!reducedMotion.matches) {
        scaleFrame = window.requestAnimationFrame(runScaleLoop);
      }
    }

    function restartScaleLoop() {
      if (!shouldScale) return;

      if (scaleFrame) {
        window.cancelAnimationFrame(scaleFrame);
        scaleFrame = null;
      }

      updateScales();

      if (!reducedMotion.matches) {
        scaleFrame = window.requestAnimationFrame(runScaleLoop);
      }
    }

    function rebuild() {
      rebuildFrame = null;
      clearClones(track);

      const baseItems = getBaseItems(track);
      outer.classList.remove(READY_CLASS);
      outer.classList.toggle("is-reduced-motion", reducedMotion.matches);

      if (!baseItems.length) return;

      const loopDistance = measureLoopDistance(track, baseItems);
      track.style.setProperty("--trustmarks-loop-distance", `${loopDistance}px`);

      if (!loopDistance || reducedMotion.matches) {
        track.style.removeProperty("--trustmarks-loop-duration");
        restartScaleLoop();
        return;
      }

      const visibleWidth = Math.ceil(outer.getBoundingClientRect().width);
      const cloneSetCount = Math.max(1, Math.ceil(visibleWidth / loopDistance));
      const duration = Math.max(loopDistance / speed, 1);

      cloneItems(track, baseItems, cloneSetCount);
      track.style.setProperty(
        "--trustmarks-loop-duration",
        `${duration.toFixed(2)}s`,
      );
      outer.classList.add(READY_CLASS);
      restartScaleLoop();
    }

    function scheduleRebuild() {
      if (rebuildFrame) {
        window.cancelAnimationFrame(rebuildFrame);
      }

      rebuildFrame = window.requestAnimationFrame(rebuild);
    }

    getAllItems(track).forEach((item) => {
      item.querySelectorAll("img").forEach((image) => {
        if (!image.complete) {
          image.addEventListener("load", scheduleRebuild, { once: true });
        }
      });
    });

    if ("ResizeObserver" in window) {
      new ResizeObserver(scheduleRebuild).observe(outer);
    } else {
      window.addEventListener("resize", scheduleRebuild);
    }

    if ("addEventListener" in reducedMotion) {
      reducedMotion.addEventListener("change", scheduleRebuild);
    } else {
      reducedMotion.addListener(scheduleRebuild);
    }

    window.addEventListener("load", scheduleRebuild, { once: true });
    onReady(scheduleRebuild);
  }

  window.ResillionTrustmarkMarquee = {
    init: initTrustmarkMarquee,
  };
})();
